import { NgStyle, TitleCasePipe } from '@angular/common';
import { Component, effect, inject, OnDestroy, untracked } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CartStore, userAddressToAddress } from '@apps/mtjr/customers/app/cart/cart.store';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonRadio,
  IonRadioGroup,
  IonText,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { VxErrorBarComponent } from '@libs/core/lib/error-view/error-bar.component';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { VxSubmitButtonComponent } from '@libs/core/lib/form-controls/mtjr-submit-button.component';

import { VxCurrencyPipe } from '@libs/core/lib/pipes/mtjr-currency.pipe';
import { VxDynamicImageComponent } from '@libs/core/lib/shared/mtjr-image.component';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { Entity, PaymentMethodList } from '@libs/mtjr/shared/generated/types';
import { NotificationsStore } from '@libs/mtjr/shared/notifications/notifications.store';
import { UserStore } from '@libs/mtjr/shared/user/user.store';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'mac-checkout',
  imports: [
    IonLabel,
    IonIcon,
    IonButtons,
    IonBackButton,
    IonContent,
    IonList,
    IonItem,
    IonText,
    IonButton,
    VxCurrencyPipe,
    IonCard,
    IonHeader,
    IonCard,
    IonToolbar,
    IonTitle,
    NgStyle,
    VxDynamicImageComponent,
    VxSubmitButtonComponent,
    VxErrorBarComponent,
    TitleCasePipe,
    IonRadioGroup,
    IonRadio,
    TranslatePipe,
    RouterLink,
  ],
  templateUrl: './checkout.page.html',
})
export class CheckoutPage implements OnDestroy {
  cartStore = inject(CartStore);
  appAlerts = inject(AppAlerts);
  userStore = inject(UserStore);
  eventBus = inject(NgEventBus);
  router = inject(Router);
  modalCtrl = inject(ModalController);
  notificationsStore = inject(NotificationsStore);
  paymentMethods = PaymentMethodList;
  route = inject(ActivatedRoute);
  routeSub?: Subscription;

  constructor() {
    this.userStore.fetchUserIfNeeded();
    effect(() => {
      if (this.userStore.addresses().length) {
        untracked(() => {
          this.cartStore.setAddress(this.userStore.addresses()[0]);
        });
      }
    });

    effect(() => {
      const address = this.cartStore.address();
      const items = this.cartStore.items();
      if (address && items.length && !this.cartStore.onlyDigitalProducts()) {
        untracked(() => {
          this.cartStore.calculateShippingCost({
            customerAddress: userAddressToAddress(address),
            orderProducts: items.map((item) => {
              const vendor = item.product.vendor;
              const vendorAddress = {
                id: vendor.id,
                label: vendor.addressLabel || '',
                street: vendor.addressStreet || '',
                internalAddress: vendor.addressInternal || null,
                phone: vendor.addressPhone || '',
                phone2: vendor.addressPhone2 || null,
                latitude: vendor.addressLat || 0,
                longitude: vendor.addressLng || 0,
              };
              return {
                productSize: item.product.size ?? 'medium',
                vendorAddress: vendorAddress,
                quantity: item.quantity,
              };
            }),
          });
        });
      }
    });

    effect(() => {
      const orderState = this.cartStore.placeOrderState();
      if (orderState.success) {
        untracked(() => {
          this.appAlerts.successSnackbarTR('CHECKOUT.ORDER_PLACED_SUCCESS');
          this.cartStore.reset();
          this.router.navigate(['/orders', orderState.data.id]);
          this.eventBus.cast('orders:fetch');
        });
      }
    });
    this.routeSub = this.route.queryParams.subscribe((params) => {
      const selectedAddrId = params['selectedAddrId'];
      if (selectedAddrId) {
        const address = this.userStore.addresses().find((addr) => addr.id === selectedAddrId);
        if (address) {
          this.cartStore.setAddress(address);
        }
        this.router.navigate([], {
          queryParams: { selectedAddrId: null },
          relativeTo: this.route,
          replaceUrl: true,
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  onAddressSelected(address?: Entity.UserAddress) {
    if (address) {
      this.cartStore.setAddress(address);
    }
  }

  getUniqueVendors(): Entity.Vendor[] {
    const vendorMap = new Map<string, Entity.Vendor>();

    this.cartStore.items().forEach((item) => {
      if (item.product.vendor && !vendorMap.has(item.product.vendor.id)) {
        vendorMap.set(item.product.vendor.id, item.product.vendor);
      }
    });

    return Array.from(vendorMap.values());
  }

  async checkout() {
    if (!this.notificationsStore.isGranted()) {
      await this.notificationsStore.requestPermission({
        showAlert: true,
        trMessage: 'NOTIFICATIONS.ALLOW_NOTIFICATIONS_FOR_PURCHASES',
      });
    }

    if (!this.cartStore.onlyDigitalProducts()) {
      if (!this.cartStore.address()) {
        this.appAlerts.dangerSnackbarTR('CHECKOUT.MISSING_DELIVERY_ADDRESS');
        return;
      }
    }
    this.cartStore.checkout();
  }

  ngOnDestroy(): void {
    this.modalCtrl.dismiss();
    this.routeSub?.unsubscribe();
  }
}
